import { Component, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { CompanyApi } from '@app/services/company-api';
import { CompanyInterface } from '@app/interface/company';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import {
  PaginatedResponse,
  RecursiveInterface,
} from '@app/services/base-authenticated.service';
import { InvoiceApi } from '@app/services/invoice-api';
import {
  InvoiceInterface,
  PensionFundOptions,
  DocumentTypeOptions,
} from '@app/interface/invoice';
// import { LayoutComponent } from '@app/layout/layout.component';
import { MatDividerModule } from '@angular/material/divider';
import { ArubaService } from '@app/services/aruba.service';
import { BackHeaderComponent } from '@app/back-header/back-header.component';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { ConfirmInvoiceDuplicateComponent } from '@app/confirm-duplicate-dialog/confirm-duplicate-dialog.component';
import { MatChipListbox, MatChipOption } from '@angular/material/chips';
import { MatChipsModule } from '@angular/material/chips';
import { LanguageOptions } from '@app/interface/language-options';
import { StatusOptions } from '@app/interface/status-options';
import { IvaCollectabilityOptions } from '@app/interface/iva-collectability-options';
import { PaymentMethodOptions } from '@app/interface/payment-method-options';
import { CurrencyOption } from '@app/interface/currency-options';
import { MatSelectModule } from '@angular/material/select';
import { ProductserviceApiService } from '@app/services/productservice-api.service';
import { ProductServiceInterface } from '@app/interface/product-service';
import { MatTableModule } from '@angular/material/table';
import { MatExpansionModule } from '@angular/material/expansion';
import { PreventiveApiService } from '@app/services/preventive-api.service';
import { SelectPdfStyleDialogComponent } from '@app/select-pdf-style-dialog/select-pdf-style-dialog.component';
import { ConfirmEmailSendDialogComponent } from '@app/confirm-email-send-dialog/confirm-email-send-dialog.component';
import { ToastService } from '@app/services/toast.service';

@Component({
  selector: 'izy-preventive-details',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    // LayoutComponent,
    MatDividerModule,
    BackHeaderComponent,
    MatDatepickerModule,
    MatTooltipModule,
    MatIconModule,
    MatChipsModule,
    MatSelectModule,
    MatTableModule,
    MatExpansionModule,
  ],
  templateUrl: './preventive-details.component.html',
  styleUrl: './preventive-details.component.scss',
})
export class PreventiveDetailsComponent {
  private toast = inject(ToastService);
  invoiceForm: FormGroup;
  invoiceId: number;
  isLoading = true;
  currentInvoice: InvoiceInterface | null = null;

  linkedCompany: {
    id: number;
    company_name: string;
    iva_number: string;
    address: string;
    addressee_code: string;
  } | null = null;
  company_name: string | undefined;
  company_address: string | undefined;
  company_iva_number: string | undefined;
  company_addressee_code: string | undefined;
  company_id!: number;

  // Product services properties
  productServices: ProductServiceInterface[] = [];
  displayedColumns: string[] = [
    'name',
    'quantity',
    'units',
    'gross_amount',
    'discount',
    'iva_percentage',
    'iva_amount',
    'final_amount',
  ];
  isLoadingProducts = false;

  // dict:
  currencyOptions: CurrencyOption[] = [
    { code: 'USD', name: 'US Dollar' },
    { code: 'EUR', name: 'Euro' },
    { code: 'GBP', name: 'British Pound' },
    { code: 'JPY', name: 'Japanese Yen' },
    { code: 'CAD', name: 'Canadian Dollar' },
    { code: 'AUD', name: 'Australian Dollar' },
    { code: 'CNY', name: 'Chinese Yuan' },
    { code: 'INR', name: 'Indian Rupee' },
  ];

  paymentMethodOptions: PaymentMethodOptions[] = [
    { code: 'MP01', name: 'Contanti' },
    { code: 'MP02', name: 'Assegno' },
    { code: 'MP03', name: 'Assegno circolare' },
    { code: 'MP04', name: 'Contanti presso Tesoreria' },
    { code: 'MP05', name: 'Bonifico' },
    { code: 'MP06', name: 'Vaglia cambiario' },
    { code: 'MP07', name: 'Bollettino bancario' },
    { code: 'MP08', name: 'Carta di credito' },
    { code: 'MP09', name: 'RID' },
    { code: 'MP10', name: 'RID utenze' },
    { code: 'MP11', name: 'RID veloce' },
    { code: 'MP12', name: 'Riba' },
    { code: 'MP13', name: 'MAV' },
    { code: 'MP14', name: 'Quietanza erario stato' },
    { code: 'MP15', name: 'Giroconto su conti di contabilità speciale' },
    { code: 'MP16', name: 'Domiciliazione bancaria' },
    { code: 'MP17', name: 'Domiciliazione postale' },
  ];

  ivaCollectabilityOptions: IvaCollectabilityOptions[] = [
    { code: 'IMMEDIATA', name: 'Immediata' },
    { code: 'DIFFERITA', name: 'Differita' },
    { code: 'SPLIT', name: 'Split payment' },
  ];

  languageOptions: LanguageOptions[] = [
    { code: 'it', name: 'Italiano' },
    { code: 'en', name: 'Inglese' },
    { code: 'fr', name: 'Francese' },
    { code: 'de', name: 'Tedesco' },
    { code: 'es', name: 'Spagnolo' },
  ];

  pensionFundOptions: PensionFundOptions[] = [
    {
      code: 'TC01',
      name: 'Cassa nazionale previdenza e assistenza avvocati e procuratori legali',
    },
    { code: 'TC02', name: 'Cassa previdenza dottori commercialisti' },
    { code: 'TC03', name: 'Cassa previdenza e assistenza geometri' },
    {
      code: 'TC04',
      name: 'Cassa nazionale previdenza e assistenza ingegneri e architetti liberi professionisti',
    },
    { code: 'TC05', name: 'Cassa nazionale del notariato' },
    {
      code: 'TC06',
      name: 'Cassa nazionale previdenza e assistenza ragionieri e periti commerciali',
    },
    {
      code: 'TC07',
      name: 'Ente nazionale assistenza agenti e rappresentanti di commercio (ENASARCO)',
    },
    {
      code: 'TC08',
      name: 'Ente nazionale previdenza e assistenza consulenti del lavoro (ENPACL)',
    },
    {
      code: 'TC09',
      name: 'Ente nazionale previdenza e assistenza medici (ENPAM)',
    },
    {
      code: 'TC10',
      name: 'Ente nazionale previdenza e assistenza farmacisti (ENPAF)',
    },
    {
      code: 'TC11',
      name: 'Ente nazionale previdenza e assistenza veterinari (ENPAV)',
    },
    {
      code: 'TC12',
      name: "Ente nazionale previdenza e assistenza impiegati dell'agricoltura (ENPAIA)",
    },
    {
      code: 'TC13',
      name: 'Fondo previdenza impiegati imprese di spedizione e agenzie marittime',
    },
    {
      code: 'TC14',
      name: 'Istituto nazionale previdenza giornalisti italiani (INPGI)',
    },
    {
      code: 'TC15',
      name: 'Opera nazionale assistenza orfani sanitari italiani (ONAOSI)',
    },
    {
      code: 'TC16',
      name: 'Cassa autonoma assistenza integrativa giornalisti italiani (CASAGIT)',
    },
    {
      code: 'TC17',
      name: 'Ente previdenza periti industriali e periti industriali laureati (EPPI)',
    },
    {
      code: 'TC18',
      name: 'Ente previdenza e assistenza pluricategoriale (EPAP)',
    },
    {
      code: 'TC19',
      name: 'Ente nazionale previdenza e assistenza biologi (ENPAB)',
    },
    {
      code: 'TC20',
      name: 'Ente nazionale previdenza e assistenza professione infermieristica (ENPAPI)',
    },
    {
      code: 'TC21',
      name: 'Ente nazionale previdenza e assistenza psicologi (ENPAP)',
    },
    { code: 'TC22', name: 'INPS' },
  ];

  statusOptions: StatusOptions[] = [
    { code: 'TRASMESSA', name: 'Trasmessa' },
    { code: 'ATTESA', name: 'Atttesa' },
  ];

  documentTypeOptions: DocumentTypeOptions[] = [
    { code: 'TD01', name: 'Fattura' },
    { code: 'TD04', name: 'Nota di credito' },
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private invoiceService: InvoiceApi,
    private preventiveService: PreventiveApiService,
    private companyService: CompanyApi,
    private fb: FormBuilder,
    private arubaService: ArubaService,
    private dialog: MatDialog,
    private productServiceApi: ProductserviceApiService,
  ) {
    this.invoiceForm = this.fb.group({
      invoice_name: ['', Validators.required],
      emission_date: [''],
      payment_date: [''],
      progressive_number: [1],
      enumeration: 'Primary',
      organization_id: [1],
      company_id: [''], // , Validators.required],
      description: [''],
      description_internal: [''],
      articles_number: [1],
      gross_amount: [0.0],
      amount: [0.0],
      iva_percentage: [0],
      iva_amount: [0.0],
      discount: [0.0],
      discount_amount: [0.0],
      addressee_code: [''],
      addressee_name: [''],
      is_paid: [false],
      language: [''],
      currency: [''],
      p_a: [false],
      payment_method: [''],
      iban: [''],
      status: [''],
      revenue_stamp: [0.0],
      revenue_stamp_for_company: [false],
      iva_collectability: [''],
      revenge: [0.0],
      revenge_rate: [0.0],
      pension_fund: [0.0],
      pension_fund_rate: [0.0],
      taxable_amount: [0.0],
      withholding_advance_tax: [0.0],
      withholding_advance_tax_rate: [0.0],
      withholding_tax: [0.0],
      withholding_tax_rate: [0.0],
      pension_fund_type: [''],
      healthcare_invoice: [false],
      document_type: ['TD01'],
      notes: [''],
      is_draft: [false],
      is_deleted: [false],
      is_preventive: [false],
      validity_date: [''],
    });

    this.invoiceId = +this.route.snapshot.params['id'];
  }

  ngOnInit(): void {
    this.loadInvoiceData();
    // this.loadCompany();
  }

  private loadInvoiceData(): void {
    const organization_id = Number(
      this.route.snapshot.paramMap.get('organizationId'),
    );

    this.preventiveService
      .getAllPreventives<InvoiceInterface>(organization_id)
      .subscribe({
        next: (response: RecursiveInterface<InvoiceInterface>) => {
          const invoice = response.results.find((c) => c.id === this.invoiceId);
          if (invoice) {
            this.currentInvoice = invoice;
            this.invoiceForm.patchValue(invoice);

            // Set the company data from the invoice
            this.linkedCompany = invoice.company || null;
            this.company_id = invoice.company_id;
            this.company_name = invoice.company?.company_name;
            this.company_address = invoice.company?.address;
            this.company_iva_number = invoice.company?.iva_number;
            this.company_addressee_code = invoice.company?.addressee_code;

            this.isLoading = false;

            // Load product services after invoice data is loaded
            this.loadProductServices(organization_id);
          } else {
            throw new Error('Invoice not found');
          }
        },
        error: (err: any) => {
          console.error('Error loading invoice:', err);
          this.showErrorMessage('Errore durante il caricamento.');
          this.router.navigate(['/invoice_table']);
        },
      });
  }

  formatFormDate(controlName: string): string {
    const dateValue = this.invoiceForm.get(controlName)?.value;
    if (!dateValue) return '';

    // If it's already a string in dd/MM/yyyy format, return as is
    if (typeof dateValue === 'string' && dateValue.includes('/')) {
      return dateValue;
    }

    // Format the date
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '';

    return date.toLocaleDateString('it-IT'); // dd/MM/yyyy
  }

  // private loadCompany(): void {
  //   const organization_id = Number(
  //     this.route.snapshot.paramMap.get('organizationId')
  //   );

  //   // const company_id = this.invoiceForm.value.company_id;
  //   // console.log(this.invoiceForm.value.company_id);
  //   // console.log(company_id);

  //   this.isLoading = true;

  //   console.log('check', this.company_id);

  //   this.companyService
  //     .getSpecificCompany<CompanyInterface>(organization_id, this.company_id)
  //     .subscribe({
  //       next: (response: any) => {
  //         this.linkedCompany = response; // or use find() with specific ID
  //         this.company_name = this.linkedCompany?.company_name;

  //         this.isLoading = false;
  //       },
  //       error: (err: any) => {
  //         this.isLoading = false;
  //         console.error('Error loading companies:', err);
  //         this.showErrorMessage('Errore nel caricamento dei clienti');
  //       },
  //     });
  // }

  private loadProductServices(organizationId: number): void {
    this.isLoadingProducts = true;

    this.productServiceApi
      .getAllProductsServices<ProductServiceInterface>(
        this.invoiceId,
        organizationId,
      )
      .subscribe({
        next: (response: RecursiveInterface<ProductServiceInterface>) => {
          // Ensure numeric fields are actually numbers
          this.productServices = response.results.map((product) => ({
            ...product,
            gross_amount: this.safeNumberConvert(product.gross_amount),
            iva_amount: this.safeNumberConvert(product.iva_amount),
            final_amount: this.safeNumberConvert(product.final_amount),
            quantity: this.safeNumberConvert(product.quantity),
            discount: this.safeNumberConvert(product.discount),
            iva_percentage: this.safeNumberConvert(product.iva_percentage),
          }));
          this.isLoadingProducts = false;
          console.log('Loaded product services:', this.productServices);
        },
        error: (err: any) => {
          console.error('Error loading product services:', err);
          this.isLoadingProducts = false;
          this.showErrorMessage('Errore nel caricamento dei prodotti/servizi');
        },
      });
  }

  // Helper method to safely convert to number
  private safeNumberConvert(value: any): number {
    if (value === null || value === undefined) return 0;

    // If it's already a number, return it
    if (typeof value === 'number') return value;

    // If it's a string, try to convert it
    if (typeof value === 'string') {
      // Remove any extra formatting that might cause issues
      const cleanValue = value.replace(/[^\d.,]/g, '').replace(',', '.');

      // Remove multiple decimal points (keep only the first one)
      const parts = cleanValue.split('.');
      if (parts.length > 2) {
        value = parts[0] + '.' + parts.slice(1).join('');
      } else {
        value = cleanValue;
      }

      const num = parseFloat(value);
      return isNaN(num) ? 0 : num;
    }

    return 0;
  }

  get displayDocumentType(): string {
    const docTypeCode = this.invoiceForm.get('document_type')?.value;
    const found = this.documentTypeOptions.find(
      (option) => option.code === docTypeCode,
    );
    return found ? `${found.name} (${found.code})` : 'Seleziona tipo documento';
  }

  get displayPaymentMethodType(): string {
    const payTypeCode = this.invoiceForm.get('payment_method')?.value;
    const found = this.paymentMethodOptions.find(
      (option) => option.code === payTypeCode,
    );
    return found ? `${found.name} (${found.code})` : 'Seleziona tipo pagamento';
  }

  getLanguageDisplayName(): string {
    const langCode = this.invoiceForm.get('language')?.value;
    if (!langCode) return '';

    const langOption = this.languageOptions.find(
      (option) => option.code === langCode,
    );
    return langOption ? langOption.name : langCode;
  }

  getIvaCollectabilityDisplayName(): string {
    const ivaCode = this.invoiceForm.get('iva_collectability')?.value;
    if (!ivaCode) return '';

    const ivaOption = this.ivaCollectabilityOptions.find(
      (option) => option.code === ivaCode,
    );
    return ivaOption ? ivaOption.name : ivaCode;
  }

  getPensionFundDisplayName(): string {
    const pensionFundCode = this.invoiceForm.get('pension_fund_type')?.value;
    if (!pensionFundCode) return '';

    const pensionFundOption = this.pensionFundOptions.find(
      (option) => option.code === pensionFundCode,
    );
    return pensionFundOption ? pensionFundOption.name : pensionFundCode;
  }

  getBooleanDisplay(value: boolean | null | undefined): string {
    if (value === null || value === undefined) return 'Non specificato';
    return value ? 'Sì' : 'No';
  }

  // method to your InvoiceDetailComponent class
  getTotalProductServices(): number {
    return this.productServices.reduce(
      (total, product) => total + (product.final_amount || 0),
      0,
    );
  }

  async duplicateInvoice(): Promise<void> {
    const organization_id = Number(
      this.route.snapshot.paramMap.get('organizationId'),
    );

    if (!this.currentInvoice) {
      this.showErrorMessage(
        'Impossibile duplicare: dati fattura non disponibili',
      );
      return;
    }

    const dialogRef = this.dialog.open(ConfirmInvoiceDuplicateComponent, {
      width: '90vw',
      maxWidth: '400px',
      panelClass: 'izy-confirm-dialog-panel',
    });
    const result = await dialogRef.afterClosed().toPromise();

    if (!result) {
      // User cancelled the dialog
      return;
    }

    // Create duplicate data manually
    const duplicateData = {
      ...this.currentInvoice,
      invoice_name: `${this.currentInvoice.invoice_name} (Copia)`,
      is_draft: true,
      is_paid: false,
      emission_date: new Date().toISOString().split('T')[0],
      // Remove fields that should not be duplicated
      id: undefined,
      created_at: undefined,
      updated_at: undefined,
    };

    this.preventiveService
      .createPreventive(organization_id, duplicateData)
      .subscribe({
        next: (newInvoice: InvoiceInterface) => {
          this.showSuccessMessage(
            `Fattura ${
              this.currentInvoice!.invoice_name
            } duplicata con successo!`,
          );

          // Navigate to the new duplicated invoice
          const org_id = this.route.snapshot.paramMap.get('organizationId');
          this.router.navigate([`/${org_id}/preventive`, newInvoice.id]);
        },
        error: (err: any) => {
          console.error('Error creating duplicate invoice:', err);
          this.showErrorMessage('Impossibile duplicare la fattura');
        },
      });
  }

  downloadPdf(invoiceId: number): void {
    // Open the dialog first
    const dialogRef = this.dialog.open(SelectPdfStyleDialogComponent, {
      // un 500px secco sul telefono sborderebbe dallo schermo
      width: '90vw',
      maxWidth: '500px',
      data: {
        title: 'Scarica PDF Preventivo',
        message: 'Seleziona lo stile del PDF che preferisci.',
      },
    });

    // Handle dialog result
    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.download) {
        try {
          // Pass the selected style to your service
          this.preventiveService.downloadPreventivePdf(invoiceId, result.style);
          this.showSuccessMessage(
            `La fattura è stata scaricata sul dispositivo con successo!`,
          );
        } catch (error) {
          this.showErrorMessage('Errore download PDF');
        }
      }
    });
  }

  downloadXml(): void {
    try {
      this.preventiveService.downloadpreventiveXml(this.invoiceId);
      this.showSuccessMessage(
        `La fattura è stata scaricata sul dispositivo con successo!`,
      );
    } catch (error) {
      this.showErrorMessage('Errore download XML');
    }
  }

  async sendInvoiceEmail(): Promise<void> {
    const organization_id = Number(
      this.route.snapshot.paramMap.get('organizationId'),
    );

    // Check if currentInvoice is not null
    if (!this.currentInvoice) {
      this.showErrorMessage('Nessuna fattura selezionata.');
      return;
    }

    const dialogRef = this.dialog.open(ConfirmEmailSendDialogComponent, {
      width: '90vw',
      maxWidth: '400px',
      height: '400px',
      data: {
        email_address: this.currentInvoice.company.email_address,
        company_id: this.currentInvoice.company.id,
        organization_id: organization_id,
      },
    });
    const result = await dialogRef.afterClosed().toPromise();

    if (!result) {
      // User cancelled the dialog
      return;
    }

    try {
      this.preventiveService.sendEmailWithPDF(
        organization_id,
        this.currentInvoice?.id,
      );
      this.showSuccessMessage(
        `Il preventivo è stato inviato al cliente con successo!`,
      );
    } catch (error) {
      this.showErrorMessage('Errore invio email con PDF.');
    }
  }

  onCancel(): void {
    const org_id = this.route.snapshot.paramMap.get('organizationId');
    this.router.navigate([`/${org_id}/preventives_table`]);
  }

  private showSuccessMessage(message: string): void {
    this.toast.success(message);
  }

  private showErrorMessage(message: string): void {
    this.toast.error(message);
  }
}
